import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Please connect Supabase first.');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    flowType: 'pkce',
    detectSessionInUrl: false,
  },
});

// Guard so safeLocalSignOut doesn't run concurrently / repeatedly in a loop.
let isSigningOut = false;

/**
 * Helper: safely signs out locally ONLY (no server call).
 * Use this when the refresh token is already known to be invalid.
 */
export async function safeLocalSignOut() {
  if (isSigningOut) return;
  isSigningOut = true;
  try {
    await supabase.auth.signOut({ scope: 'local' });
  } catch {
    // If even local signOut fails, manually wipe the storage key
    try {
      const keys = Object.keys(localStorage);
      for (const key of keys) {
        if (key.startsWith('sb-') && key.includes('auth-token')) {
          localStorage.removeItem(key);
        }
      }
    } catch {
      // last resort — nothing more we can do
    }
  } finally {
    isSigningOut = false;
  }
}

/**
 * Detect whether an error is a dead/expired refresh-token auth error.
 * These are harmless (just means the session is gone), so we want to
 * swallow them and sign out locally instead of letting them surface
 * as uncaught exceptions / error-tracker noise.
 */
function isAuthRefreshError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as any;
  const msg = String(e?.message || e?.name || '').toLowerCase();
  return (
    e?.__isAuthError === true &&
    (msg.includes('refresh token') ||
      msg.includes('refresh_token') ||
      msg.includes('session has been expired') ||
      msg.includes('session not found'))
  );
}

// Global guard for unhandled promise rejections (e.g. background auto-refresh).
window.addEventListener('unhandledrejection', (event) => {
  const reason: unknown = event.reason;
  if (isAuthRefreshError(reason)) {
    event.preventDefault();
    console.warn(
      '[Supabase] Swallowed unhandled refresh-token error (signing out locally):',
      (reason as any)?.message || reason,
    );
    safeLocalSignOut();
  }
});

// Global guard for uncaught exceptions.
window.addEventListener('error', (event) => {
  if (isAuthRefreshError(event.error)) {
    event.preventDefault();
    console.warn(
      '[Supabase] Swallowed uncaught auth error (signing out locally):',
      (event.error as any)?.message || event.error,
    );
    safeLocalSignOut();
  }
});

// On startup: check if session is still valid.
// If the refresh token is dead, supabase throws. We catch it and just
// wipe local session data — no need to call signOut() because the server
// already considers the token invalid.
(async () => {
  try {
    const { error } = await supabase.auth.getSession();
    if (error) {
      console.warn('[Supabase] Stale session on startup, clearing local state:', error.message);
      await safeLocalSignOut();
    }
  } catch (err: any) {
    console.warn('[Supabase] Session check threw, clearing local state:', err?.message || err);
    await safeLocalSignOut();
  }
})();

// Listen for SIGNED_OUT events (e.g. token expired, admin revoked session)
// and clear any app state that depends on auth.
supabase.auth.onAuthStateChange((event) => {
  if (event === 'SIGNED_OUT') {
    console.log('[Supabase] SIGNED_OUT event — session cleared');
    // Additional cleanup can go here if needed
  }
});

export default supabase;